import React, {useEffect} from "react";
import AsyncSelect from "react-select/async";
import {useMapsLibrary} from "@vis.gl/react-google-maps";
import {useField} from "formik";
import {SelectOption} from "../types/SelectOption";
import {SingleValue} from "react-select";
import {useErrorBoundary} from "react-error-boundary";

interface PlacesAutoCompleteInputProps {
    name: string;
    id?: string;
    disabled?: boolean;
    setManualSearchLocationDefPlaceID: React.Dispatch<React.SetStateAction<string | null>>;
}

const PlacesAutoCompleteInput: React.FC<PlacesAutoCompleteInputProps> = (props) => {

    const [field, _, helpers] = useField(props.name);
    const placesLib = useMapsLibrary("places");
    const [sessionToken, setSessionToken] = React.useState<google.maps.places.AutocompleteSessionToken>();

    const {showBoundary} = useErrorBoundary<Error>();

    // Create a new session token when the places library is loaded
    useEffect(() => {

        if (!placesLib) {
            return;
        }

        // Create a new session token
        setSessionToken(new placesLib.AutocompleteSessionToken());

    }, [placesLib]);

    // Fetch autocomplete options and return as a Promise
    const getPlacesAutoCompleteOptions = async (inputValue: string): Promise<SelectOption[]> => {
        if (!placesLib || inputValue.length === 0) {
            return [];
        }

        try {

            const request = {
                input: inputValue,
                language: "en-US",
                region: "us",
                sessionToken: sessionToken, // Create a session token
            };

            // Use AutocompleteSuggestion API to fetch suggestions
            const suggestions = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

            const options = suggestions.suggestions.map((suggestion) => {
                const placePrediction = suggestion.placePrediction;

                if (!placePrediction) return null;

                return {
                    label: placePrediction.text.text,
                    value: placePrediction.placeId
                }
            });

            // Filter out null results
            return options.filter((option): option is SelectOption => option !== null);

        } catch (error) {
            console.error("Error fetching autocomplete suggestions:", error);
            showBoundary(error as Error);
            return [];
        }
    };

    // AsyncSelect's loadOptions function
    const loadOptions = (inputValue: string, callback: (options: SelectOption[]) => void) => {
        getPlacesAutoCompleteOptions(inputValue).then(callback);
    };

    return (
        <AsyncSelect
            className="blj-store-locator__form-select"
            inputId={props.id}
            loadOptions={loadOptions}
            placeholder="Enter city, state or ZIP code..."
            isClearable
            cacheOptions
            defaultOptions
            noOptionsMessage={({inputValue}) =>
                inputValue ? "No results found" : "Start typing to search..."
            }
            {...field}
            onChange={(option: SingleValue<SelectOption>) => {
                helpers.setValue(option);

                if (option !== null) {
                    props.setManualSearchLocationDefPlaceID(option.value);
                }

            }}
            isDisabled={props.disabled}
        />
    );

}

export default PlacesAutoCompleteInput;